import { formatDate } from "@angular/common";
import { inject, Injectable, signal } from "@angular/core";
import { Tag } from "@models/common/tag";
import { CellDataType } from "@models/grid/cell-data-type.enum";
import { IndividualRecord } from "@models/individuals/Individual-record";
import { warningStateFilterOptions, warningStatesFilterValues } from "@models/individuals/individuals-enums";
import { PageResult } from "@models/requests/page-result.interface";
import { AgDateCellComponent } from "@shared/components/grid/ag-cells/common/ag-date-cell/ag-date-cell.component";
import { AgEntityTagsComponent } from "@shared/components/grid/ag-cells/common/ag-entity-tags/ag-entity-tags.component";
import {
  AgIndividualNameComponent
} from "@shared/components/grid/ag-cells/common/ag-individual-name/ag-individual-name.component";
import { CountryPipe } from "@shared/pipes/countryPipe";
import { DinPipe } from "@shared/pipes/din.pipe";
import { PhonePipe } from "@shared/pipes/phonePipe";
import { BaseGridService } from "@shared/services/base/base-grid.service";
import { IndividualsApiService } from "@shared/services/individuals-api.service";
import { applyTagGridFilterOption } from "@shared/utils/grid/apply-tag-grid-filter-option";
import { dateComparator } from "@shared/utils/grid/date-comparator";
import { uniqueArrayToFilterOptionHelper } from "@shared/utils/unique-array-to-filter-option.helper";
import { ColDef, IServerSideDatasource, IServerSideGetRowsParams, ValueFormatterParams } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { of, Subject } from "rxjs";

@Injectable()
export class IndividualsGridService extends BaseGridService {
  private individualsApiService = inject(IndividualsApiService);
  private toastr = inject(ToastrService);

  totalRecords = signal<number>(0);
  dataSource = signal<IServerSideDatasource>(this.getDataSource());

  dataLoaded$ = new Subject<PageResult<IndividualRecord>>();
  refreshFiltersTrigger$ = new Subject<void>();

  // filters
  tagsList: string[] = [];
  showIndividualVerification = false;

  setGridFilterOptions(
    tagsList: string[],
    showIndividualVerification: boolean
  ): void {
    this.tagsList = tagsList;
    this.showIndividualVerification = showIndividualVerification;
  }

  refreshFilters(): void {
    this.refreshFiltersTrigger$.next();
  }

  setDataSource(searchText?: string, endRow?: number): void {
    this.dataSource.set(this.getDataSource(searchText, endRow));
  }

  getDataSource(searchText?: string, endRow?: number): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        const request = params.request;

        if (endRow) {
          request.endRow = endRow;
        }

        this.individualsApiService.getGridList(request, searchText)
          .subscribe({
            next: (result) => {
              this.totalRecords.set(result.total);

              params.success({
                rowData: result.records,
                rowCount: result.total,
              });

              this.dataLoaded$.next(result);
            },
            error: (err) => {
              this.toastr.error('Failed to load individuals', 'Error');
              console.error(err);
              params.fail();
              return of([]);
            },
          });
      },
    };
  }

  override setColDefs(actionColumn: ColDef): void {
    const colDefs: ColDef[] = [
      {
        headerName: 'Name',
        field: 'fullNameStored',
        width: 150,
        flex: 1,
        editable: false,
        sort: 'asc',
        suppressMovable: true,
        cellRenderer: AgIndividualNameComponent,
        cellRendererParams: {
          showIndividualVerification: this.showIndividualVerification
        },
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Email address',
        field: 'email',
        width: 150,
        flex: 1,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Mobile number',
        field: 'phone',
        width: 150,
        valueFormatter: (params: ValueFormatterParams<IndividualRecord, string>) => PhonePipe.formatValue(params.data?.phone),
        flex: 1,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Director ID',
        field: 'din',
        width: 150,
        valueFormatter: (params: ValueFormatterParams<IndividualRecord, string>) => DinPipe.formatValue(params.data?.din),
        flex: 1,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Date of Birth',
        field: 'dob',
        width: 90,
        flex: 1,
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: dateComparator,
        },
        valueFormatter: (params: ValueFormatterParams<IndividualRecord, Date>) => {
          return params.value ? formatDate(params.value ?? '', 'dd MMM yyyy', 'en-US') : '-';
        },
        cellRenderer: AgDateCellComponent,
        cellDataType: CellDataType.DATE,
        hide: true,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Place of Birth',
        field: 'birthCity',
        width: 90,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams<IndividualRecord>) => CountryPipe.formatPlaceOfBirth(params.data?.birthCity, params.data?.birthCountry, '-'),
        hide: true,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Tags',
        field: 'tags',
        width: 150,
        flex: 1,
        sortable: false,
        filterParams: {
          filterOptions: [],
        },
        cellRendererParams: {
          allowEdit: true,
          handleAddNewTag: this.refreshFilters.bind(this),
          onClickTag: this.onClickTag.bind(this)
        },
        cellRenderer: AgEntityTagsComponent,
        valueFormatter: (params: { value: Tag }) => params.value?.name ?? '',
        menuTabs: [],
      },
      {
        headerName: 'Tags',
        field: 'tagsName',
        suppressColumnsToolPanel: true,
        lockVisible: true,
        sortable: false,
        hide: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: this.tagsList,
          filterOptions: uniqueArrayToFilterOptionHelper(this.tagsList),
        },
        cellRendererParams: { allowEdit: false },
        menuTabs: [],
      },
      {
        headerName: 'Waning states',
        field: 'warningStatesName',
        suppressColumnsToolPanel: true,
        lockVisible: true,
        sortable: false,
        hide: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: warningStateFilterOptions,
          values: warningStatesFilterValues,
        },
        cellRendererParams: { allowEdit: false },
        menuTabs: [],
      },
      actionColumn
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true, } as ColDef));

    this.colDefs.set(colDefs);
  }

  onClickTag(tag: Tag): void {
    applyTagGridFilterOption(tag, this.colDefs, this.gridApi, 'tagsName');
  }
}
